import React, { ChangeEvent, useContext } from 'react';
import { Col, Form, Layout, Row, Tooltip } from 'antd';
import zipcodes from 'zipcodes';
import { Input, Select, Upload, InputNumber, CityAutoSuggest } from 'components';
import { ReactComponent as IconInfo } from 'static/icons/info_outlined.svg';
import { TeamRegistrationContext } from '../TeamRegistration';
import './style.scss';

const seasonLabel = (
  <>
    What season are you applying for?
    <Tooltip
      classNames={{ root: 'tooltip-root' }}
      title="This is just an initial intention — you’ll be able to discuss all the details with our manager by phone later and change your decision if needed."
    >
      <IconInfo className="info-icon" />
    </Tooltip>
  </>
);

const formatStates = (state: string) =>
  state
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const states = Object.entries(zipcodes.states.abbr).map(([value, label]) => ({
  label: formatStates(label),
  value,
}));

export const Step1 = () => {
  const form = Form.useFormInstance();

  const { divisionsOptions, seasonsOptions } = useContext(TeamRegistrationContext);

  const handleZipChange = (e: ChangeEvent<HTMLInputElement>) => {
    const zip = e.target.value;
    if (zip.length === 5) {
      const location = zipcodes.lookup(zip);
      if (location) {
        form.setFieldsValue({
          city: location.city,
          state: location.state,
        });
      }
    }
  };

  return (
    <Layout className="team-registration__step-1">
      <Row gutter={40}>
        <Col sm={{ span: 24 }} md={{ flex: '160px' }}>
          <Upload
            crop
            name="logo"
            label="Team logo"
            formItemProps={{
              extra: '.png, .jpg, .jpeg, .heic — max 10MB, min 256×256px. Square and transparent background preferred.',
            }}
            rules={[{ required: true, message: 'Upload team logo' }]}
          />
        </Col>
        <Col flex="1">
          <Row gutter={8}>
            <Col sm={24} md={{ flex: '100%' }}>
              <Input
                name="name"
                label="Team Name"
                rules={[
                  { required: true, message: 'Enter Team Name' },
                  { min: 1, max: 50, message: 'Length must be between 1 and 50' },
                ]}
              />
            </Col>
          </Row>
          <Row gutter={8} style={{ marginBottom: 16 }}>
            <Col flex="72px">
              <Input
                name="zip_code"
                label="Zip Code"
                rules={[
                  { required: true, message: 'Enter Zip' },
                  {
                    pattern: /^\d{5}$/,
                    message: '5 digits required',
                  },
                ]}
                onChange={handleZipChange}
              />
            </Col>
            <Col sm={24} md={{ flex: '1' }}>
              <CityAutoSuggest name="city" label="City" rules={[{ required: true, message: 'Enter City' }]} />
            </Col>
            <Col sm={24} md={{ flex: '1' }}>
              <Select
                showSearch
                name="state"
                label="State"
                rules={[{ required: true, message: 'Select State' }]}
                options={states}
              />
            </Col>
          </Row>
          <Row gutter={8}>
            <Col className="division">
              <Select
                name="division"
                label="Division of Play"
                rules={[{ required: true, message: 'Select division' }]}
                options={divisionsOptions}
              />
            </Col>
            <Col flex="1">
              <Select
                name="anticipated_season"
                label={seasonLabel}
                rules={[{ required: true, message: 'Select season' }]}
                options={seasonsOptions}
              />
            </Col>
          </Row>
          <Form.Item shouldUpdate={(prev, curr) => prev.division !== curr.division} noStyle>
            {({ getFieldValue }) => {
              if (getFieldValue('division') !== 'Academy') return null;
              return (
                <Row>
                  <Col span={24}>
                    <InputNumber
                      name="numU13"
                      min={0}
                      max={99}
                      label="Number of U13 Teams"
                      rules={[{ required: true, message: 'Input number' }]}
                    />
                  </Col>
                  <Col span={24}>
                    <InputNumber
                      name="numU15"
                      min={0}
                      max={99}
                      label="Number of U15 Teams"
                      rules={[{ required: true, message: 'Input number' }]}
                    />
                  </Col>
                  <Col span={24}>
                    <InputNumber
                      min={0}
                      max={99}
                      name="numU17"
                      label="Number of U17 Teams"
                      rules={[{ required: true, message: 'Input number' }]}
                    />
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
        </Col>
      </Row>
    </Layout>
  );
};
